import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Grid, Paper, Typography, alpha, Stack, Divider } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import request from '../../services/Request';

const ModernStatCard = ({ title, value, color, gradient, previousValue, isFirst }) => {
  const theme = useTheme();
  const change = previousValue ? (((value - previousValue) / previousValue) * 100).toFixed(1) : 0;
  const isPositive = change >= 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        height: '100%',
        borderRadius: 3.2,
        position: 'relative',
        backdropFilter: 'blur(8px)',
        background: `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(color, 0.05)})`,
        border: `0.8px solid ${alpha(color, 0.25)}`,
        boxShadow: `0 6.4px 16px ${alpha(color, 0.2)}`, // 0.8
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4.8px)', // -6 * 0.8
          boxShadow: `0 12.8px 24px ${alpha(color, 0.3)}`, // 0.8
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at top right, ${alpha(
            color,
            0.25
          )} 0%, transparent 60%)`,
          opacity: 0.3,
          pointerEvents: 'none',
        },
      }}
    >
      <Stack spacing={1.6} sx={{ position: 'relative', zIndex: 1 }}>
        {/* Title */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 0.8,
              fontSize: '0.52rem',
              textTransform: 'uppercase',
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Amount with Trend */}
        <Stack direction="row" alignItems="baseline" spacing={1.2}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              background: gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.6rem', sm: '2rem' }, // 0.8
              lineHeight: 1,
            }}
          >
            ${value || '0.00'}
          </Typography>

          {previousValue && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                px: 0.8,
                py: 0.4,
                borderRadius: 1.6,
                backgroundColor: isPositive
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.error.main, 0.1),
              }}
            >
              {isPositive ? (
                <TrendingUp sx={{ fontSize: 14.4, color: theme.palette.success.main }} />
              ) : (
                <TrendingDown sx={{ fontSize: 14.4, color: theme.palette.error.main }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem', // Scaled
                  color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                }}
              >
                {Math.abs(change)}%
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ borderColor: alpha(color, 0.15) }} />

        {/* Bottom Text */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.68rem',
            }}
          >
            Revenue generated
          </Typography>
          {previousValue && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.56rem',
              }}
            >
              vs prev
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default function ModernChart(props) {
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));
  const useremail = user?.email;
  const userRole = user?.role;
  const { id, view_trans } = props;
  const [data, setData] = useState([0, 0, 0]);

  const date = new Date();
  const today = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999)
  );
  const newD = new Date(today);
  const yesterday = newD.setUTCDate(newD.getUTCDate() - 1);
  const dayBefore = newD.setUTCDate(newD.getUTCDate() - 1);
  const finalDate = newD.setUTCDate(newD.getUTCDate() - 1);

  useEffect(() => {
    if (userRole === 'SuperAdmin') return;
    const run = async () => {
      if (view_trans) {
        await request({
          method: 'post',
          url: `/orders/getRecentData`,
          data: {
            email: useremail,
            referTo: id,
            startDate: new Date(finalDate),
            endDate: new Date(today),
          },
        }).then((res) => {
          let pToday = 0;
          let pYesterday = 0;
          let pBefore = 0;
          res.data?.forEach((order) => {
            const orderDate = new Date(order.createdAt);

            if (orderDate > new Date(yesterday)) {
              pToday += order.total;
            } else if (orderDate > new Date(dayBefore)) {
              pYesterday += order.total;
            } else {
              pBefore += order.total;
            }
          });

          setData([pToday.toFixed(2), pYesterday.toFixed(2), pBefore.toFixed(2)]);
        });
      }
    };
    run();
    return () => {
      setData([]);
    };
  }, [id]);

  const totalRevenue = (parseFloat(data[0]) + parseFloat(data[1]) + parseFloat(data[2])).toFixed(2);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.6, mt: 1.6 }}
        flexWrap="wrap"
        gap={1.6}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              mb: 0.4,
              fontSize: '1.2rem', // Scaled h5
              color: theme.palette.text.primary,
            }}
          >
            Revenue Overview
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={500}
            sx={{ fontSize: '0.7rem' }}
          >
            Last 3 days performance
          </Typography>
        </Box>

        {/* Total Summary Badge */}
        <Box
          sx={{
            px: 2,
            py: 0.8,
            borderRadius: 2.4,
            background:
              theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: `0 3.2px 12.8px ${alpha(theme.palette.primary.main, 0.25)}`, // 0.8
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 600,
              display: 'block',
              mb: 0.24,
              letterSpacing: 0.4,
              fontSize: '0.52rem',
            }}
          >
            TOTAL (3 DAYS)
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: '1.2rem', // Scaled
              color: 'white',
              textShadow: '0 1.6px 6.4px rgba(0,0,0,0.15)', // 0.8
            }}
          >
            ${totalRevenue}
          </Typography>
        </Box>
      </Stack>

      {/* Stats Grid */}
      <Grid container spacing={1.6} mt={3.2}>
        {' '}
        {/* 2*0.8, 4*0.8 */}
        <Grid item xs={12} sm={4}>
          <ModernStatCard
            title="Today"
            value={data[0]}
            previousValue={data[1]}
            color={theme.palette.primary.main}
            gradient={`linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`}
            isFirst={true}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ModernStatCard
            title="Yesterday"
            value={data[1]}
            previousValue={data[2]}
            color={theme.palette.secondary.main}
            gradient={`linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ModernStatCard
            title="Day Before"
            value={data[2]}
            previousValue={null}
            color={theme.palette.info.main}
            gradient={`linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
